import { Link } from "react-router-dom";
import { ArrowRight, Check, Star, Shield, Clock, Award, Bath, Wrench, ShowerHead } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BathroomRefitSEOPage = ({ location }) => {
  const trustItems = [
    { icon: Star, value: "5.0", label: "Google Rating" },
    { icon: Shield, value: "Fully", label: "Insured" },
    { icon: Clock, value: "Free", label: "Quotations" },
    { icon: Award, value: "15+", label: "Years Experience" }
  ];

  const services = [
    "Full bathroom renovation",
    "Shower installation",
    "Bath installation",
    "Toilet installation",
    "Basin installation",
    "Wall and floor tiling",
    "Bathroom layout updates",
    "Plumbing adjustments",
    "Vanity unit fitting",
    "Heated towel rail installation"
  ];

  const process = [
    { step: 1, title: "Free Consultation", description: "We visit your home to discuss your vision and assess the space." },
    { step: 2, title: "Detailed Quote", description: "Receive a comprehensive, no-obligation quote with full breakdown." },
    { step: 3, title: "Design Planning", description: "Work with you to finalise fixtures, tiles, and layout." },
    { step: 4, title: "Professional Installation", description: "Our skilled team completes your bathroom transformation." },
    { step: 5, title: "Final Inspection", description: "Thorough check to ensure everything meets our high standards." }
  ];

  const faqs = [
    { q: "How long does a bathroom refit take?", a: "A typical bathroom refit takes 5-10 days depending on the scope of work. We'll provide a timeline during your consultation." },
    { q: "Do you handle all the plumbing?", a: "Yes, we handle all plumbing work including relocating pipes and fixtures if needed." },
    { q: "Can you help choose fixtures and tiles?", a: "Absolutely! We can recommend suppliers and help you select fixtures that match your budget and style." },
    { q: "Do you dispose of the old bathroom?", a: "Yes, removal and disposal of your old bathroom suite is included in our service." }
  ];

  return (
    <div data-testid={`bathroom-refit-seo-${location.toLowerCase()}`}>
      {/* Hero */}
      <section className="seo-hero">
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-primary-foreground/80 mb-4 uppercase tracking-wider text-sm">
                {location} Bathroom Specialists
              </p>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-montserrat">
                Bathroom Renovation & Refit in {location}
              </h1>
              <p className="text-lg text-white/80 mb-8">
                Complete bathroom renovation services including removal of existing suites, plumbing adjustments, tiling installation, fixture installation and finishing. Transform your bathroom with DC Tiling.
              </p>
              <Link to="/bathroom-refit">
                <Button size="lg" className="bg-white text-primary hover:bg-gray-100" data-testid="refit-seo-cta-primary">
                  Estimate My Bathroom Refit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="hidden lg:block">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758448018619-4cbe2250b9ad?w=800" 
                  alt={`Bathroom renovation in ${location}`} 
                  className="w-full h-full object-cover" 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustItems.map((item, index) => (
              <div key={index} className="trust-item">
                <div className="trust-icon">
                  <item.icon className="w-6 h-6" />
                </div>
                <p className="font-bold text-2xl text-gray-900">{item.value}</p>
                <p className="text-gray-500">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-montserrat">
                Bathroom Refit Services in {location}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Full bathroom renovation services including removal of existing suites, plumbing adjustments, tiling installation, fixture installation and finishing.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {services.map((service, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
              <Link to="/bathroom-refit">
                <Button className="bg-primary hover:bg-primary/90" data-testid="refit-seo-services-cta">
                  Estimate My Bathroom Refit
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-[3/4] rounded-xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1758548157466-7c454382035a?w=600" 
                    alt="Modern bathroom" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[3/4] rounded-xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1771239048280-0724031de3a4?w=600" 
                    alt="Bathroom renovation" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              Our Bathroom Refit Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From consultation to completion, we make your bathroom renovation hassle-free
            </p>
          </div>
          
          <div className="grid md:grid-cols-5 gap-6">
            {process.map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Guide */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              Bathroom Refit Pricing
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Get an instant ballpark estimate for your bathroom renovation project
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Small Bathroom</h3>
                <p className="text-3xl font-bold text-primary mb-2">£3,500 - £5,500</p>
                <p className="text-sm text-gray-500">Up to 4 sqm</p>
              </div>
              <div className="bg-primary/10 rounded-xl p-6 border-2 border-primary">
                <h3 className="font-semibold text-gray-900 mb-2">Medium Bathroom</h3>
                <p className="text-3xl font-bold text-primary mb-2">£5,500 - £8,500</p>
                <p className="text-sm text-gray-500">4-8 sqm</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Large Bathroom</h3>
                <p className="text-3xl font-bold text-primary mb-2">£8,500 - £12,000</p>
                <p className="text-sm text-gray-500">8+ sqm</p>
              </div>
            </div>
            
            <Link to="/bathroom-refit">
              <Button size="lg" className="bg-primary hover:bg-primary/90" data-testid="refit-seo-pricing-cta">
                Get Your Detailed Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center font-montserrat">
            Frequently Asked Questions
          </h2>
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible>
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-medium">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-montserrat">
            Ready for Your New Bathroom?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Get a ballpark estimate in minutes and book your free bathroom consultation today.
          </p>
          <Link to="/bathroom-refit">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100" data-testid="refit-seo-final-cta">
              Estimate My Bathroom Refit
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BathroomRefitSEOPage;
